import { Module } from 'vuex';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/functions';
import 'firebase/auth';
import router from '@/router';
import { getRegionName } from '@/utils/functionsRegion';
import { defaultFacilityValues } from '@/consts/defaultFacilityValues';
import { loadDataToStore } from './modulesHelper';
import { CalendarFilters } from '@/models/calendar/CalendarFilters';
import { serializer } from '@/api/shared/serializer';
import { getEnabledRoute } from '@/router/router-helper';
import { isArraysEqual } from '@/utils/isArraysEqual';
import { StaffAccessLevels } from '@/enums/StaffAccessLevels';
import Intercom from '@intercom/messenger-js-sdk';
import { shutdown, update, onUnreadCountChange, showMessages } from '@intercom/messenger-js-sdk';
import { hasAccess } from '@/utils/accessControl/accessControl';
import { AccessMatrixKeys } from '@/utils/accessControl/accessMatrixKeysEnum';
import { allSettled } from '@/utils/allSettled'
import { stopIdleSessionTimer } from '@/bootstrap/initIdleSessionTimer';
import { stopActiveSessionTimer } from '@/bootstrap/initActiveSessionTimer';

const authModule: Module<any, any> = {
  state: {
    user: { name: "" },
    sessionTimeoutOpen: false,
    sessionTimeoutIsIdle: false,
    onCurrentUserUnsubscribe: null,
    premiumFeatures: {
      isStaffHubSurveys: false,
      isStaffHubContentManagerMediaLibrary: false,
      isStaffHubDevicesColumnDigitalSignage: false,
      isStaffHubContentManagerGallery: false,
      isStaffHubCalendar: false,
      isStaffHubMessenger: false,
      isStaffHubDocuments: false,

      featureDigitalSignage: false,
      featureLifestyle: false,
      featureMaintenance: false,
      featureFamilyEngagement: false,
      featureConcierge: false,

      featureNonMCareCustomers: false,
      featureMCareContactsProfiles: false,
    },
    error: null,
    currentFacility: null,
    currentFacilityData: null,
    loginFailed: false,
    facilityLoginFailed: false,
    rciUserFailed: false,
    maintenanceUserFailed: false,
    loadingSuccess: false,
    isOneSubscribeOnResidents: false,
    isChangingFacilityOnCurrentBrowser: false,
    pendingFacilityId: null,
    onNotificationsUnsubscribe: null,
    impersonationData: null,
    intercomTokenExpiresAt: null,
    facilityChangeNotice: null as null | { to: string; at: number },
    securityEnabled: false,
  },

  mutations: {
    setUser(state, payload) {
      state.user = payload;
    },
    setUserParams(state, params) {
      state.user.email = params.email;
    },
    setError(state, payload) {
      state.error = payload;
    },
    setCurrentFacilityId(state, payload) {
      state.currentFacility = payload;
    },
    setCurrentFacilityData(state, currentFacility) {
      state.currentFacilityData = currentFacility;
      state.premiumFeatures = {
        isStaffHubSurveys: currentFacility.isStaffHubSurveys,
        isStaffHubContentManagerMediaLibrary: currentFacility.isStaffHubContentManagerMediaLibrary,
        isStaffHubDevicesColumnDigitalSignage: currentFacility.isStaffHubDevicesColumnDigitalSignage,
        isStaffHubContentManagerGallery: currentFacility.isStaffHubContentManagerGallery,
        isStaffHubCalendar: currentFacility.isStaffHubCalendar,
        isStaffHubMessenger: currentFacility.isStaffHubMessenger,
        isStaffHubDocuments: currentFacility.isStaffHubDocuments,

        featureDigitalSignage: currentFacility.featureDigitalSignage,
        featureLifestyle: currentFacility.featureLifestyle,
        featureMaintenance: currentFacility.featureMaintenance,
        featureFamilyEngagement: currentFacility.featureFamilyEngagement,
        featureNonMCareCustomers: currentFacility.featureNonMCareCustomers,
        featureMCareContactsProfiles: currentFacility.featureMCareContactsProfiles,
        featureConcierge: currentFacility.featureConcierge
      }
      console.log('premiumFeatures:', state.premiumFeatures)
    },
    setLoginFailed(state, payload) {
      state.loginFailed = payload;
    },
    setFacilityLoginFailed(state, payload) {
      state.facilityLoginFailed = payload;
    },
    setMaintenanceUserFailed(state, payload) {
      state.maintenanceUserFailed = payload;
    },
    setRciUserFailed(state, payload) {
      state.rciUserFailed = payload;
    },
    setIsChangingFacilityOnCurrentBrowser(state, payload) {
      state.isChangingFacilityOnCurrentBrowser = payload;
    },
    setPendingFacilityId(state, payload) {
      state.pendingFacilityId = payload;
    },
    setFacilityChangeNotice(state, payload) { 
      state.facilityChangeNotice = payload 
    },
    clearFacilityChangeNotice(state) { 
      state.facilityChangeNotice = null 
    },
    setImpersonationData(state, value) {
      state.impersonationData = value
    },
    clearCurrentFacilityName(state) {
      state.currentFacilityData.name = "";
    },
    setLoginSuccess(state, payload) {
      state.loadingSuccess = payload;
    },
    clearAuthState(state){
      state.user = null,
      state.premiumFeatures = {
        isStaffHubSurveys: false,
        isStaffHubContentManagerMediaLibrary: false,
        isStaffHubDevicesColumnDigitalSignage: false,
        isStaffHubContentManagerGallery: false,
        isStaffHubCalendar: false,
        isStaffHubMessenger: false,
        isStaffHubDocuments: false,

        featureDigitalSignage: false,
        featureLifestyle: false,
        featureMaintenance: false,
        featureFamilyEngagement: false,
        featureConcierge: false,

        featureNonMCareCustomers: false,
        featureMCareContactsProfiles: false,
      },
      state.error = null
      state.currentFacility = null
      state.currentFacilityData = null
      state.loginFailed = false
      state.facilityLoginFailed = false
      state.maintenanceUserFailed = false
      state.loadingSuccess = false
      state.isOneSubscribeOnResidents = false
      state.isChangingFacilityOnCurrentBrowser = false
      state.onNotificationsUnsubscribe = null
      state.pendingFacilityId = null
      state.facilityChangeNotice = null
      state.securityEnabled = false
    },
    setIntercomTokenExpiresAt(state, payload) {
      state.intercomTokenExpiresAt = payload;
    },
    setSessionTimeoutOpen(state, v: boolean) {
      state.sessionTimeoutOpen = v
    },
     setSessionTimeoutIsIdle(state, v: boolean) {
      state.sessionTimeoutIsIdle = v
    },
    setSecurityEnabled(state, v: boolean) {
      state.securityEnabled = v;
    },
  },
  actions: {
    async userSignIn({ commit, dispatch, state }, payload) {
      console.log('userSignIn() started')

      commit("setLoginFailed", false);
      commit("setFacilityLoginFailed", false);
      commit("setRciUserFailed", false);
      commit("setMaintenanceUserFailed", false);

      try {
        const result = await firebase.app().functions(getRegionName()).httpsCallable("staff-signIn")({ email: payload.email, password: payload.password, sourceApp: 'StaffHub' })
        const customToken = result?.data?.customToken

        if (!customToken) {
          commit("setLoginFailed", true);
          commit("setError", "Login failed");
          console.error('userSignIn: No customToken returned')
          return false
        }

        return await firebase
          .auth()
          .signInWithCustomToken(customToken)
          .then(async firebaseUser => {
            console.log('signInWithCustomToken() success')

            if (firebaseUser.user) {
              const { claims } = await firebaseUser.user.getIdTokenResult()
              console.log("CLAIMS:", JSON.stringify(claims))
           
              const user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("users").doc(firebaseUser.user.uid).get()) as any

              try {
                  if (claims?.facilityId) {
                    user.facilityId = claims.facilityId; 
                  }

                  await dispatch("initAuthData", { user, claims })

                  if (this.getters.isSuperAdmin) {
                    console.log(this.getters.user)
                    router.push("/buddy/facilities")
                  } else if(this.getters.isMaintenanceAdmin) {
                    router.push("/maintenanceDashboard")
                  } else {
                    const enabledRoute = getEnabledRoute(this.getters.premiumFeatures)
                    router.push(enabledRoute.path);
                  }
                  return true
              } catch (error) {
                  commit("setLoginFailed", true);
                  commit("setRciUserFailed", false);
                  console.error('userSignIn: Login Failed for user:' + payload.email + ' with Exception: ' + error)
                  return false
              }
            }
          })
          .catch(error => {
            commit("setLoginFailed", true);
            commit("setError", error.message);
            console.error('userSignIn: Unhandled error', error)
            return false
          });

      } catch (error) {
        const code = error?.code || ''
        const reason = error?.details?.reason || ''

        if (reason === 'IP_NOT_ALLOWED' || code === 'permission-denied') {
          const uiMessage = error?.details?.uiMessage || "Login from this network is not allowed";
          commit("setLoginFailed", true);
          commit("setError", uiMessage);
          console.error('userSignIn: IP not allowed', error)
          return false
        }

        if (reason === 'FILE_CLOSED') {
          commit("setRciUserFailed", true);
          commit("setLoginFailed", false);
          commit("setMaintenanceUserFailed", false);
          return false;
        }

        if (reason === 'NO_FACILITIES') {
          commit("setFacilityLoginFailed", true);
          commit("setLoginFailed", false);
          commit("setRciUserFailed", false);
          console.error('userSignIn: No facilities')
          return false
        }

        if (reason === 'MAINTENANCE_USER_NOT_ALLOWED') {
          console.log("MAINTENACE USER")
          commit("setMaintenanceUserFailed", true);
          commit("setLoginFailed", false);
          commit("setRciUserFailed", false);
          console.error('userSignIn: User has "Maintenance User" access level')
          router.push("/login");
          return false
        }

        if (reason === 'IP_NOT_ALLOWED' || code === 'permission-denied') {
          commit("setLoginFailed", true);
          commit("setError", "Login from this network is not allowed");
          console.error('userSignIn: IP not allowed', error)
          return false
        }

        if (code === 'unauthenticated' || reason === 'INVALID_CREDENTIALS') {
          commit("setLoginFailed", true);
          commit("setError", "Invalid email or password");
          console.error('userSignIn: Invalid credentials', error)
          return false
        }

        commit("setLoginFailed", true);
        commit("setError", error?.message || 'Login failed');
        console.error('userSignIn: Unhandled error', error)
        return false
      }
    },

    async autoSignIn({ commit, dispatch, state }, firebaseUser) {
      if (!firebaseUser) return;
      state.isOneSubscribeOnResidents = false;

      const user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("users").doc(firebaseUser.uid).get()) as any

       if(user.fileClosed === true){
          commit("setRciUserFailed", true);
          commit("setLoginFailed", false);
          commit("setMaintenanceUserFailed", false);
          commit("clearAuthState");
          router.push("/login");
          return false;
        }

      if (!user.facilitiesIds || !user.facilitiesIds.length) {
        commit("setFacilityLoginFailed", true);
        commit("setLoginFailed", false);
        commit("setRciUserFailed", false);
        commit("clearAuthState");
        console.error('autoSignIn: No facilities')
        router.push("/login");
        return false
      }

      try {
        const { claims } = await firebaseUser.getIdTokenResult()
        const currentUserData = {...state.user}
        await dispatch("initAuthData", { user: currentUserData, claims: claims });
        await dispatch("initIntercomData", currentUserData);

        // Login logging removed - only log on manual login in userSignIn, not on auto re-auth

        return true
      } catch (error) {
        commit("setLoginFailed", true);
        commit("setRciUserFailed", false);
        console.error('autoSignIn: Login Failed for user:' + user.email + ' with Exception: ' + error)
        return false
      }
    },

    async signInByCustomToken({ commit, dispatch }, payload) {

      const customToken = payload.customToken
      const isExternalPagesViewing = payload.isExternalPagesViewing || false // user sign in by custom token to view external page like 'Set Password Screen' for Family Hub, Notice Board for TV Box etc.

      if (!customToken) {
        commit("setLoginFailed", true);
        console.error('Login Failed for custom token')
        router.push("/login");
        return false
      }

      await allSettled([
        dispatch("unsubscribeOnNotifications"),
        dispatch("unsubscribeOnResidentsCollection"),
        dispatch("unsubscribeOnCalenderEvents"),
        dispatch("unsubscribeOnCurrentFacility"),
      ]);

      return await firebase
        .auth()
        .signInWithCustomToken(customToken)
        .then(async (firebaseUser: any) => {
          if (firebaseUser.user) {
            try {
              const { claims } = await firebaseUser.user.getIdTokenResult()
              console.log('claims', claims)

              let user: any = {}
              if(claims?.isFamily === true){
                user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("familyHubUsers").doc(firebaseUser.user.uid).get()) as any
              } else if (claims?.isResident === true) {
                user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("residents").doc(firebaseUser.user.uid).get()) as any
              } else if (claims?.isStaff === true) {
                user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("users").doc(firebaseUser.user.uid).get()) as any
              
                if(user.fileClosed === true){
                  commit("setRciUserFailed", true);
                  return false;
                }
              
              } else {
                console.error("User doesn't have correct claims")
              }

              user.isExternalPagesViewing = isExternalPagesViewing
              dispatch("unsubscribeOnCurrentUser");
              await dispatch("initAuthData", { user, claims })
              return true
            } catch (error) {
              commit("setLoginFailed", true);
              console.error('Login Failed for custom token with Exception: ' + error)
              return false
            }
          }
        })
        .catch(error => {
          commit("setLoginFailed", true);
          console.error('Login Failed for custom token', error)
          router.push("/login");
          return false
        });
    },

    async stopImpersonation({ dispatch, commit, state }) {
      const impersonation = state?.impersonationData

      if(!impersonation || !impersonation?.superUserId) return

      const superAdminCustomTokenResult = await firebase.app().functions(getRegionName()).httpsCallable("getUserCustomToken")({ userId: impersonation.superUserId })
      const superAdminTokenCustomToken = superAdminCustomTokenResult?.data?.customToken;
      await dispatch("signInByCustomToken", {  customToken: superAdminTokenCustomToken })
      commit("setImpersonationData", null);

      router.push(`/buddy/facilities/${impersonation.selectedBuddyFacilityId}`)
    },

    /**
      @deprecated
      Candidate for removal.
      We are not using anonymous users anymore,
      but potentially it could be used for external meetings (virtual events)
    **/
    async signInAnonymously({ commit, dispatch }, data) {
      return await firebase.auth()
        .signInAnonymously()
        .then(async (query: any) => {
          const userId = query.user.uid;
          const isUserAnonymous = query.user.isAnonymous;

          const user = {
            name: "Guest",
            logoUrl: "",
            isAnonymous: true,
            email: data?.email || null
          };

          if (isUserAnonymous) {
            return await firebase
              .app()
              .functions(getRegionName())
              .httpsCallable("setAnonymouslyClaims")({ userId: userId, facilityId: data?.facilityId })
              .then(async (a) => {
                return await firebase.auth().currentUser.getIdTokenResult(true).then(async (idTokenResult) => {
                  const claims = idTokenResult.claims;
                  console.log('CLAIMS:', claims); 
                  commit("setUser", user);
                  return user;
                })
              });
          }
          else {
            return null
          }
        })
        .catch(error => {
          console.error(error);
          return null
        });
    },

    async changeCurrentUserFacility({ dispatch, commit, state }, { newFacilityId, redirectEnabled }) {
      console.log('CHANGING FACILITY TO: ' + newFacilityId)

      const initiatedLocally = state.isChangingFacilityOnCurrentBrowser === true && state.pendingFacilityId === newFacilityId;
      if (state.isChangingFacilityOnCurrentBrowser && !initiatedLocally) {
        console.log('Facility change already in progress (other request), skip');
        return true;
      }

      const currentFacilityId = this.state.authModule.currentFacility
      const isImpersonating = this.getters.isImpersonationMode

      if (currentFacilityId === newFacilityId) {
          if (initiatedLocally) {
            commit('setIsChangingFacilityOnCurrentBrowser', false);
            commit('setPendingFacilityId', null);
          }
          console.log('Already on requested facility, skipping reauth');
          return true;
       }
    
      await allSettled([
        dispatch("unsubscribeOnNotifications"),
        dispatch("unsubscribeOnResidentsCollection"),
        dispatch("unsubscribeOnCalenderEvents"),
        dispatch("unsubscribeOnCurrentFacility"),
      ]);

      return await firebase.app().functions(getRegionName())
        .httpsCallable("facility-changeUserFacility")({ newFacilityId: newFacilityId, prevFacilityId: currentFacilityId, impersonating: isImpersonating })
        .then(async (newFacilityResult: any) => {

          // Defensive: ensure backend returned a token.
          if(!newFacilityResult || !newFacilityResult.data || !newFacilityResult.data.customToken){
            console.log('No customToken returned from backend, aborting reauth');
            commit("setPendingFacilityId", null); 
            commit("setIsChangingFacilityOnCurrentBrowser", false);
            return false;
          }

          // Stop listeners/intercom before reauth to avoid stale state tied to old claims.
          if(state.onNotificationsUnsubscribe){
            state.onNotificationsUnsubscribe();
          }

          dispatch("destroyIntercom");

          // Immediately sign in with the fresh custom token so that local auth state has new claims.
          // This prevents any race where the client still holds an old token with the previous facilityId.
          await dispatch("signInByCustomToken", {  customToken: newFacilityResult.data.customToken })

          // Force refresh and verify that claims include the new facilityId (best-effort with small retry).
          // This guards against rare propagation lag on the client SDK.
          const retryVerifyClaims = async (expected, attempts = 10) => {
            let delay = 300;
            for (let i = 0; i < attempts; i++) {
              try {
                await firebase.auth().currentUser?.getIdToken(true);
                const res = await firebase.auth().currentUser?.getIdTokenResult(true);
                if (res?.claims && (res.claims as any).facilityId === expected) return true;
              } catch (error) {
                console.log(`error retryVerifyClaims(${i})`)
              }
              await new Promise(r => setTimeout(r, delay));
              delay = Math.min(delay * 2, 2000);
            }
            return false;
          }

          const retryVerifyClaimsResult = await retryVerifyClaims(newFacilityId, 10)
          if(!retryVerifyClaimsResult){
            console.warn('Claims did not reflect expected facilityId in time, using safe fallback');
          }

          commit("setIsChangingFacilityOnCurrentBrowser", false);
          commit("setPendingFacilityId", null);

          if (redirectEnabled === true) {
            const enabledRoute = getEnabledRoute(this.getters.premiumFeatures)
            console.log('redirect to allowed route')
            await dispatch('reloadComponent', enabledRoute.path)
            await dispatch("initIntercomData", state.user);
            return true
          }

          return true

        })
        .catch(error => {
          commit("setPendingFacilityId", null); 
          commit("setIsChangingFacilityOnCurrentBrowser", false);
          console.log('changeCurrentUserFacility() error: ', error);
          return false
        });
    },

    async refreshToken(){
      console.log('refreshing token ...');
      return await firebase.auth().currentUser.getIdToken(true).then(async () => {
        return await firebase.auth().currentUser.getIdTokenResult(true).then((idTokenResult) => {
          console.log('TOKEN REFRESHED', idTokenResult.claims);
          return idTokenResult
        })})
    },  
    
    async reloadComponent({ dispatch, commit, state }, pathToComponent: string){
      console.log('reloadComponent: ', pathToComponent);
       /*Trick to force the created hook to run again and re-render the component.
        TODO: remove this hack when we have a better solution*/
        await router.replace({ path: '/' });
        await router.replace({ path: pathToComponent });

        /* alternative way with page reload
        window.location.href = enabledRoute.path;*/
    },

    resetFailures({ dispatch, commit, state }) {
      console.log('Reset failures ...');
      commit("setLoginFailed", false)
      commit("setFacilityLoginFailed", false)
      commit("setRciUserFailed", false)
      commit("setRciUserFailed", false);
      commit("setMaintenanceUserFailed", false);
    },

    async setIsChangingFacilityOnCurrentBrowser({ dispatch, commit, state }, setIsChangingFacilityOnCurrentBrowser) {
      commit("setIsChangingFacilityOnCurrentBrowser", setIsChangingFacilityOnCurrentBrowser);
    },

    async setPendingFacilityId({ dispatch, commit, state }, id) {
      commit("setPendingFacilityId", id);
    },

    async changeUserFacility({ dispatch, commit, state }, { userId, facilityId }) {
      console.log('userId: ' + userId)
      
      return await firebase.app().functions(getRegionName()).httpsCallable("updateUserClaims")({ facilityId: facilityId, userId: userId })
    },

    async getUser({ commit, state }) {
      return await firebase
        .firestore()
        .collection("users")
        .doc(state.user.id)
        .get()
        .then(async (querySnapshot) => {
          const user = querySnapshot?.data();
          user.id = querySnapshot.id;
          return await firebase
            .auth()
            .currentUser.getIdTokenResult()
            .then(function (idTokenResult) {
              user.claims = idTokenResult.claims;
              commit("setUser", user);
              return user
            });
        });
    },

    setUserData({ commit }, user) {
      commit("setUser", user);
    },

    setImpersonation({ commit }, data){
      commit("setImpersonationData", data);
    },

    updateUserData({ commit, dispatch }, data) {
      if (data) {
        commit("setUserParams", data);
      }
    },

    userSignOut({ commit, dispatch }, data) {
      const currentUser = firebase.auth().currentUser
      if (currentUser && currentUser.isAnonymous) {
        currentUser.delete()
      }
      firebase.auth().signOut();
      localStorage.clear();
      stopIdleSessionTimer(); 
      stopActiveSessionTimer()
      dispatch("unsubscribeOnResidentsCollection");
      dispatch("unsubscribeOnCalenderEvents");
      dispatch("unsubscribeOnCurrentFacility");
      dispatch("unsubscribeOnCurrentUser");
      dispatch("unsubscribeOnNotifications");
      dispatch("clearBuddyState");
      dispatch("destroyIntercom");
      commit('setSessionTimeoutOpen', false) 
      commit('setSessionTimeoutIsIdle', false)
      commit("clearAuthState");

      if (!data?.noRedirect) {
        router.push("/login");
      }
    },

    userSignOutAnonymously({ commit, dispatch }) {
      const currentUser = firebase.auth().currentUser
      if (currentUser && currentUser.isAnonymous) {
        currentUser.delete()
      }
      firebase.auth().signOut();
      localStorage.clear();
      stopIdleSessionTimer();
      stopActiveSessionTimer()
      commit("setUser", null);
      commit("setCurrentFacilityId", null);
      commit("setLoginSuccess", false);
      dispatch("unsubscribeOnResidentsCollection");
      dispatch("unsubscribeOnCalenderEvents");
      dispatch("unsubscribeOnCurrentFacility");
      dispatch("unsubscribeOnCurrentUser");
      dispatch("unsubscribeOnNotifications");
    },

    userSignOutBySessionExpired({ commit, dispatch }) {
      const currentUser = firebase.auth().currentUser
      if (currentUser && currentUser.isAnonymous) {
        currentUser.delete()
      }
      localStorage.clear();
      stopIdleSessionTimer();
      stopActiveSessionTimer();
      dispatch("unsubscribeOnResidentsCollection");
      dispatch("unsubscribeOnCalenderEvents");
      dispatch("unsubscribeOnCurrentFacility");
      dispatch("unsubscribeOnCurrentUser");
      dispatch("unsubscribeOnNotifications");
      dispatch("clearBuddyState");
      dispatch("destroyIntercom");
      commit("clearAuthState");
    },

    async initAuthData({ commit, dispatch, state }, { user, claims }) {
      commit("setFacilityLoginFailed", false);
      const facilityId = claims?.facilityId ?? user?.facilityId;
      const userWithClaims = { ...user, facilityId, claims };
      commit("setUser", userWithClaims);

      if (!state.onResidentsUnsubscribe && !state.isOneSubscribeOnResidents && state.loadingSuccess) {
        state.isOneSubscribeOnResidents = true;
        dispatch("subscribeOnResidentsCollection");
      }

      const currentFacility = serializer.documentSnapshotToDocument(await firebase.firestore().collection("facility").doc(facilityId).get()) as any
      currentFacility.dateFormat = currentFacility.dateFormat || defaultFacilityValues.dateFormat
      currentFacility.timeFormat = currentFacility.timeFormat || defaultFacilityValues.timeFormat
      currentFacility.languageIso = currentFacility.languageIso || defaultFacilityValues.locale

      await dispatch("translate", { locale: currentFacility.languageIso });
      commit("setCurrentFacilityId", currentFacility.id);
      commit("setCurrentFacilityData", currentFacility);

      if (currentFacility.orgId) {
        const orgSnap = await firebase.firestore().collection('organisations').doc(currentFacility.orgId).get();
        commit("setSecurityEnabled", orgSnap.data()?.securityEnabled === true);
      }
      commit("setLoginSuccess", true)
      commit("setMainCalendarFilters", new CalendarFilters());
      dispatch("subscribeOnCurrentFacility", currentFacility.id);
      dispatch("subscribeOnCurrentUser");

      if(!user.isExternalPagesViewing){
        loadDataToStore()
      }
      //dispatch("getCareAPIKey ", currentFacility.id);
      commit("setLoginSuccess", true)
    },
    
    async subscribeOnCurrentUser({ commit, dispatch, state }) {

      // Check if an existing unsubscribe function is stored in state
      if (state.onCurrentUserUnsubscribe !== null || !state?.user?.id) {
        console.log("already subscribed on current user")
        return;
      }

      const query = firebase.firestore().collection("users").doc(state.user.id);
      
      console.log("Subscribe on current user")
    
      // Create and store the unsubscribe function
      state.onCurrentUserUnsubscribe = query.onSnapshot(async (snapshot) => {
        const currentUser = state.user
        const document = snapshot?.data();
        if (document) {
          document.id = snapshot.id

          if(currentUser.fileClosed !== document.fileClosed && document.fileClosed === true){
             commit("clearAuthState");
             commit("setRciUserFailed", true);
             router.push("/login");
             return
          }

          if(currentUser.name !== document.name 
            || currentUser.email !== document.email
            || this.getters.user.facilityId !== document.facilityId
            || currentUser.accessLevel !== document.accessLevel
            || currentUser.regionId !== document.regionId
            || !isArraysEqual(currentUser.facilitiesIds, document.facilitiesIds)){
            console.log(`user ${document?.id} updated`)

            if(this.getters.user.facilityId !== document.facilityId){
              console.log(`(AUTH) FACILITY CHANGED from ${ this.getters.user.facilityId } to ${document.facilityId}`)
              commit("setUser", document)

              // If the change was initiated by this browser OR matches the pending expected facility,
              // treat it as local and do not re-invoke changeCurrentUserFacility.
              if (state.isChangingFacilityOnCurrentBrowser === true || state.pendingFacilityId === document.facilityId) {
                console.log('CHANGE FACILITY - initiated from current browser, no further action needed');
                commit("setPendingFacilityId", null); 
                commit("setIsChangingFacilityOnCurrentBrowser", false); 
              } else {
                console.log('CHANGE FACILITY - initiated from another browser or via impersonation');
                commit('setFacilityChangeNotice', { to: document.facilityId, at: Date.now() });
                await dispatch('changeCurrentUserFacility', { newFacilityId: document.facilityId, redirectEnabled: true });
              }
            }
            else{
              commit("setUser", document)
            }
          }
        }
      })
    },

    async unsubscribeOnCurrentUser({state}) {
      if (state.onCurrentUserUnsubscribe) {
        state.onCurrentUserUnsubscribe()
        state.onCurrentUserUnsubscribe = null
        console.log("Unsubscribed from current user")
      }
    },

    async unsubscribeOnNotifications({ commit, state }) {
      if(state.onNotificationsUnsubscribe){
        state.onNotificationsUnsubscribe();
        state.onNotificationsUnsubscribe = null;
        console.log("Unsubscribed from notifications")
      }
    },

    async initIntercomData({ dispatch, commit, state }, user) {
      if(state.currentFacilityData && state.currentFacilityData?.isUsingIntercom && user && hasAccess(user.accessLevel, AccessMatrixKeys.Auth_IntercomAccess)) {
        try {
          const IntercomTokenData = await dispatch("getIntercomToken", user);

          if(!IntercomTokenData) return
          
          const IntercomData = await dispatch("getIntercomData");
          const TokenExpiresAt = IntercomTokenData.expiresAt;

          if (IntercomData) {
            return new Promise((resolve, reject) => {
              try {
                Intercom({
                  app_id: IntercomData.app_id,
                  region: IntercomData.region,
                  user_id: user.id,
                  name: user.name,
                  email: user.email,
                  created_at: user.createdDateTime,
                  intercom_user_jwt: IntercomTokenData.token,
                });

                let resolved = false;

                const listener = (count) => {
                  if (!resolved) {
                    resolved = true;
                    if (count > 0) {
                      showMessages();
                    }
                    resolve(count);
                  }
                };

                onUnreadCountChange(listener);
              } catch (error) {
                console.error(error);
                reject(error);
              }
            }).then(() => {
              commit("setIntercomTokenExpiresAt", TokenExpiresAt);
              dispatch("scheduleRefreshIntercomToken");
            })
          }
        } catch (error) {
          console.error(error);
          throw error;
        }
      }
    },

    async getIntercomData() {
      try {
        return await firebase
          .firestore()
          .collection("supportConfig")
          .where("type", "==", "intercom")
          .get()
          .then((querySnapshot) => {
            return querySnapshot.docs[0].data();
          });
      } catch (error) {
        console.error(error);
        return null;
      }
    },

    async getIntercomToken(_, user) {
      try {
        return await firebase
          .app()
          .functions(getRegionName())
          .httpsCallable("intercom-getJWTToken")({ user: {
            id: user.id,
            email: user.email,
            name: user.name,
            facilitiesIds: user.facilitiesIds,
            orgId: user.orgId,
            createdDateTime: user.createdDateTime
          }}).then(res => {
            if(res.data?.status === "success") {
              return res.data;
            }
            return null;
          })
      } catch (error) {
        console.error(error);
        return null;
      }
    },

    scheduleRefreshIntercomToken({ dispatch, state }) {
      const delay = state.intercomTokenExpiresAt * 1000 - Date.now();

      setTimeout(() => {
        dispatch("refreshIntercomToken");
      }, delay);
    },

    async refreshIntercomToken({ dispatch, state, commit }) {
      if(state.user) {
        const IntercomTokenData = await dispatch("getIntercomToken", state.user);
        const TokenExpiresAt = IntercomTokenData.expiresAt;

        update({
          intercom_user_jwt: IntercomTokenData.token
        });

        commit("setIntercomTokenExpiresAt", TokenExpiresAt);
        dispatch("scheduleRefreshIntercomToken");
      } else {
        dispatch("destroyIntercom");
      }
    },

    destroyIntercom({ commit }) {
      shutdown();
      (Intercom as any)('shutdown');
      commit("setIntercomTokenExpiresAt", null);
    },
    openSessionTimeout({ commit }, isIdle) { 
      if (router.currentRoute.meta?.skipSessionTimeout) return;
      commit('setSessionTimeoutIsIdle', !!isIdle)
      commit('setSessionTimeoutOpen', true)
    },
    closeSessionTimeout({ commit }) { 
      commit('setSessionTimeoutOpen', false) 
      commit('setSessionTimeoutIsIdle', false)
    }
  },

  getters: {
    user(state) {
      return state.user;
    },

    premiumFeatures(state) {
      return state.premiumFeatures;
    },

    isAuthenticated(state) {
      return (
        state.user !== null &&
        state.user !== undefined &&
        state.user?.name !== null &&
        state.user?.name?.length > 1 &&
        state.loadingSuccess === true
      );
    },

    isImpersonationMode(state) {
      return state?.impersonationData?.superUserId?.length > 0
    },

    isSuperAdmin(state) {
      return state?.user?.claims?.accessLevel == "SuperAdmin";
    },

    isAnonymous(state) {
      return state.user.isAnonymous;
    },

    isExternalPagesViewing(state) {
      return state.user?.isExternalPagesViewing;
    },

    isMaintenanceAdmin(state) {
      return state?.user?.claims?.accessLevel === StaffAccessLevels.MaintenanceAdmin;
    },

     isSessionTimeoutOpen: (s) => s.sessionTimeoutOpen,
     sessionTimeoutIsIdle: (s) => s.sessionTimeoutIsIdle,
     securityEnabled: (s) => s.securityEnabled === true,

  }
};
export default authModule;
